import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, CircularProgress, Alert,
  Card, CardContent, Divider, Tooltip, useMediaQuery, useTheme
} from '@mui/material';
import {
  ArrowBack as BackIcon, Receipt as ReceiptIcon, Check as CheckIcon,
  Warning as WarningIcon, AccountBalanceWallet as WalletIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import SettlementService from '../../services/settlement.service';

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

const SettlementDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settlement, setSettlement] = useState(null);

  useEffect(() => { fetchSettlement(); }, [id]);

  const fetchSettlement = async () => {
    setLoading(true);
    try {
      const data = await SettlementService.getById(id);
      setSettlement(data);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load settlement details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  if (error) return <Box><Alert severity="error" sx={{ mb: 2 }}>{error}</Alert><Button startIcon={<BackIcon />} onClick={() => navigate('/settlements')}>Back</Button></Box>;
  if (!settlement) return <Box><Alert severity="warning" sx={{ mb: 2 }}>Settlement not found</Alert><Button startIcon={<BackIcon />} onClick={() => navigate('/settlements')}>Back</Button></Box>;

  const getModeChip = () => {
    if (settlement.mode === 'AUTO') return <Chip label="Auto (Oldest First)" size="small" color="info" sx={{ fontSize: fontSize.xs, height: 22 }} />;
    return <Chip label="Custom Allocation" size="small" color="secondary" sx={{ fontSize: fontSize.xs, height: 22 }} />;
  };

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button size="small" startIcon={<BackIcon />} onClick={() => navigate('/settlements')}
            sx={{ fontSize: fontSize.sm }}>{!isMobile && 'Back'}</Button>
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ fontSize: isMobile ? '1rem' : '1.125rem', fontWeight: 600, color: 'secondary.main' }}>
                {settlement.settlementNumber}
              </Typography>
              {getModeChip()}
            </Box>
            <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary' }}>{formatDate(settlement.date)}</Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Customer + Wallet Info */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 2 }}>Settlement Details</Typography>
            <Divider sx={{ mb: 2 }} />

            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 0.5 }}>Customer</Typography>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{settlement.customerName || '—'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 0.5 }}>Mode</Typography>
                {getModeChip()}
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 0.5 }}>Payment Method</Typography>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{SettlementService.getPaymentMethodLabel(settlement.paymentMethod)}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 0.5 }}>Date</Typography>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{formatDate(settlement.date)}</Typography>
              </Grid>
            </Grid>

            {/* Wallet Deducted */}
            <Box sx={{ mt: 3 }}>
              <Card sx={{ bgcolor: isDark ? 'rgba(244,67,54,0.08)' : '#ffebee', border: '1px solid', borderColor: isDark ? 'rgba(244,67,54,0.2)' : '#ef9a9a' }}>
                <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                    <WalletIcon sx={{ fontSize: 16, color: isDark ? '#ef5350' : '#c62828' }} />
                    <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', fontWeight: 600 }}>Wallet Deducted</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, color: isDark ? '#ef5350' : '#c62828' }}>
                    -{SettlementService.formatCurrency(settlement.totalAmount)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {settlement.notes && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', mb: 0.5 }}>Notes</Typography>
                <Typography sx={{ fontSize: fontSize.sm }}>{settlement.notes}</Typography>
              </Box>
            )}
          </Paper>

          {/* Settled Invoices Table */}
          <Paper sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <ReceiptIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
              <Typography sx={{ fontSize: fontSize.md, fontWeight: 600 }}>
                Settled Invoices ({settlement.invoices?.length || 0})
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Invoice #</TableCell>
                    <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Date</TableCell>
                    <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Amount Settled</TableCell>
                    <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Receipt #</TableCell>
                    <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600, color: 'text.secondary' }}>Status After</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {settlement.invoices?.map((inv) => (
                    <TableRow key={inv.invoiceId} hover>
                      <TableCell sx={{ py: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'secondary.main', cursor: 'pointer' }}
                          onClick={() => navigate(`/invoices/${inv.invoiceId}`)}>
                          {inv.invoiceNumber}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: '13px', py: 1, color: 'text.secondary' }}>{formatDate(inv.invoiceDate)}</TableCell>
                      <TableCell align="right" sx={{ py: 1 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#4caf50' }}>{SettlementService.formatCurrency(inv.amountSettled)}</Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1 }}>
                        <Tooltip title="View Receipt">
                          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: isDark ? '#42a5f5' : '#1565c0', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={() => navigate(`/receipts/${inv.receiptId}`)}>
                            {inv.receiptNumber}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ py: 1 }}>
                        {inv.statusAfter === 'PAID'
                          ? <Chip icon={<CheckIcon sx={{ fontSize: 14 }} />} label="PAID" size="small" color="success" sx={{ fontSize: fontSize.xs, height: 22 }} />
                          : <Chip icon={<WarningIcon sx={{ fontSize: 14 }} />} label="PARTIAL" size="small" color="warning" sx={{ fontSize: fontSize.xs, height: 22 }} />
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Total row */}
                  <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.04)' : '#fafafa' }}>
                    <TableCell colSpan={2} sx={{ py: 1.5 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>Total</Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 0.75 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: isDark ? '#4caf50' : '#2e7d32' }}>
                        {SettlementService.formatCurrency(settlement.totalAmount)}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>

        {/* Right Column - Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ position: isMobile ? 'static' : 'sticky', top: 20 }}>
            <CardContent>
              <Typography sx={{ fontSize: fontSize.md, fontWeight: 600, mb: 1.5 }}>Summary</Typography>
              <Divider sx={{ mb: 2 }} />

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Total Invoices</Typography>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600 }}>{settlement.invoices?.length || 0}</Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Mode</Typography>
                {getModeChip()}
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Payment Method</Typography>
                <Chip label={SettlementService.getPaymentMethodLabel(settlement.paymentMethod)} size="small" sx={{ fontSize: fontSize.xs, height: 22 }} />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography sx={{ fontSize: fontSize.sm, color: 'text.secondary' }}>Wallet Deducted</Typography>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: '#f44336' }}>
                  -{SettlementService.formatCurrency(settlement.totalAmount)}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography sx={{ fontSize: fontSize.md, fontWeight: 600 }}>Total Settled</Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: isDark ? '#4caf50' : '#2e7d32' }}>
                  {SettlementService.formatCurrency(settlement.totalAmount)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SettlementDetail;
